/* Multiple instances and Shopify editor re-renders are handled by the custom element lifecycle. */
(() => {
  if (customElements.get('thq-customer-hub')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const categories = value => String(value || '').split(',').map(normalize).filter(Boolean);
  class CustomerHub extends HTMLElement {
    connectedCallback() {
      // Deferred script usually runs after parsing; the frame also handles dynamically inserted sections.
      this.frame = requestAnimationFrame(() => this.initialize());
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      this.controller?.abort();
    }
    initialize() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.search = this.querySelector('[data-hub-search]');
      this.category = '';
      this.format = '';
      const categoryNames = new Map(Array.from(this.querySelectorAll('[data-category]'), button => [normalize(button.dataset.category), button.textContent.trim()]));
      this.cards = Array.from(this.querySelectorAll('[data-hub-card]'), element => {
        const keys = categories(element.dataset.categories);
        const format = normalize(element.dataset.format);
        return {
          element, keys, format,
          text: normalize([element.dataset.search, element.dataset.categories, element.dataset.format, ...keys.map(key => categoryNames.get(key) || '')].join(' '))
        };
      });
      const formats = this.querySelector('[data-hub-formats]');
      if (formats) {
        formats.replaceChildren();
        const labels = new Map([['', this.dataset.allFormats]]);
        this.cards.forEach(card => {
          if (card.format && !labels.has(card.format)) labels.set(card.format, card.element.dataset.format);
        });
        labels.forEach((label, value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'thq-hub__filter';
          button.dataset.formatFilter = value;
          button.textContent = label;
          button.setAttribute('aria-pressed', String(value === ''));
          formats.append(button);
        });
      }
      this.querySelectorAll('[data-hub-controls]').forEach(element => { element.hidden = false; });
      this.dataset.ready = '';
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-category')) {
          this.category = normalize(button.dataset.category);
          this.applyFilters();
        } else if (button.hasAttribute('data-format-filter')) {
          this.format = button.dataset.formatFilter;
          this.applyFilters();
        } else if (button.hasAttribute('data-hub-reset')) {
          this.reset();
          // Reset controls become hidden; restore focus to a visible control.
          (this.search || this.querySelector('[data-category=""]') || this.querySelector('[data-format-filter=""]'))?.focus();
        }
      }, {signal});
      this.search?.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.applyFilters(), 120);
      }, {signal});
      this.search?.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.search.value = '';
          this.applyFilters();
        }
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.element.dataset.blockId === event.detail?.blockId);
        if (card) {
          this.reset();
          card.element.scrollIntoView({block:'nearest', behavior:'auto'});
        }
      }, {signal});
      this.applyFilters();
    }
    reset() {
      clearTimeout(this.timer);
      if (this.search) this.search.value = '';
      this.category = '';
      this.format = '';
      this.applyFilters();
    }
    applyFilters() {
      const terms = normalize(this.search?.value).split(/\s+/).filter(Boolean);
      let count = 0;
      this.cards.forEach(card => {
        const visible = (!this.category || card.keys.includes(this.category))
          && (!this.format || card.format === this.format)
          && terms.every(term => card.text.includes(term));
        card.element.hidden = !visible;
        if (visible) count++;
      });
      this.querySelectorAll('[data-category]').forEach(button => button.setAttribute('aria-pressed', String(normalize(button.dataset.category) === this.category)));
      this.querySelectorAll('[data-format-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.formatFilter === this.format)));
      const countElement = this.querySelector('[data-hub-count]');
      if (countElement) countElement.textContent = count + ' ' + this.dataset.countLabel;
      this.querySelector('[data-hub-empty]').hidden = count !== 0;
      const active = Boolean(terms.length || this.category || this.format);
      this.querySelectorAll('[data-hub-reset]').forEach(button => {
        if (!button.closest('[data-hub-empty]')) button.hidden = !active;
      });
    }
  }
  customElements.define('thq-customer-hub', CustomerHub);
})();

